import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Printer, Filter, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatBs, formatUsd, formatCop } from "@/lib/format";

// Agrupa familias reales del catálogo a las etiquetas de juego del reporte QA.
const FAMILY_LABELS = { animalitos: "Animalitos", chance: "Loterías", tradicional: "Triples" };
const todayISO = () => new Date(new Date().getTime() - 4 * 3600 * 1000).toISOString().slice(0, 10); // Caracas UTC-4

// Sufijo de campo del backend y formato por divisa. COP: solo tickets emitidos en COP (enteros).
const CUR = {
  BS: { label: "Bs", suffix: "bs", fmt: (v) => `Bs ${formatBs(v || 0)}`, active: "border-primary bg-primary/15 text-primary" },
  USD: { label: "USD", suffix: "usd", fmt: (v) => `$ ${formatUsd(v || 0)}`, active: "border-emerald-600 bg-emerald-600/15 text-emerald-700 dark:text-emerald-300" },
  COP: { label: "COP", suffix: "cop", fmt: (v) => `$ ${formatCop(v || 0)}`, active: "border-[#8f1433] bg-[#8f1433] text-white" },
};

export default function PosReportsModal({ open, onClose }) {
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [currency, setCurrency] = useState("BS");
  const [applied, setApplied] = useState({ date_from: todayISO(), date_to: todayISO(), currency: "BS" });

  // Cada apertura arranca en el día actual de Caracas.
  useEffect(() => {
    if (!open) return;
    const t = todayISO();
    setDateFrom(t); setDateTo(t); setApplied((a) => ({ ...a, date_from: t, date_to: t }));
  }, [open]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["pos-reports-cuadre", applied.date_from, applied.date_to, applied.currency],
    queryFn: async () => (await api.get("/reports/cuadre", { params: applied })).data,
    enabled: open,
    retry: (n, err) => (err?.response?.status ?? 500) >= 500 && n < 2,
  });
  const [lastTaq, setLastTaq] = useState(null);
  useEffect(() => { if (data?.taquilla) setLastTaq(data.taquilla); }, [data]);
  const taq = data?.taquilla || lastTaq;
  const taqLabel = taq ? `${taq.name}${taq.code ? ` (${taq.code})` : ""}` : null;

  const cur = CUR[applied.currency];
  const isCop = applied.currency === "COP";
  const round = (v) => (isCop ? Math.round(v) : v);
  const fmt = (v) => cur.fmt(round(Number(v) || 0));
  const totals = (!isError && data?.totals) || {};
  const pick = (obj, base) => Number(obj?.[`${base}_${cur.suffix}`]) || 0;

  // Totales en la divisa activa
  const venta = pick(totals, "venta");
  const premios = pick(totals, "premios");
  const pagado = pick(totals, "pagado");
  const porPagar = pick(totals, "por_pagar");
  const total = (Number(venta) || 0) - (Number(premios) || 0);

  // Desglose por juego (agrupando por familia)
  const porJuego = useMemo(() => {
    const buckets = {};
    for (const p of (!isError && data?.por_producto) || []) {
      const label = FAMILY_LABELS[p.family] || "Otros";
      if (!buckets[label]) buckets[label] = { label, venta: 0, premios: 0 };
      buckets[label].venta += pick(p, "venta");
      buckets[label].premios += pick(p, "premios");
    }
    const rows = Object.values(buckets).map((b) => ({ ...b, utilidad: b.venta - b.premios }));
    return rows.sort((a, b) => b.venta - a.venta);
  }, [data, isError, applied.currency]); // eslint-disable-line react-hooks/exhaustive-deps

  const totJuegoVenta = porJuego.reduce((s, r) => s + r.venta, 0);
  const totJuegoPrem = porJuego.reduce((s, r) => s + r.premios, 0);
  const totJuegoUtil = porJuego.reduce((s, r) => s + r.utilidad, 0);

  const doFilter = () => {
    const next = { date_from: dateFrom, date_to: dateTo, currency };
    if (next.date_from === applied.date_from && next.date_to === applied.date_to && next.currency === applied.currency) refetch();
    else setApplied(next);
  };
  const doPrint = () => window.print();

  const RESUMEN = [
    { label: "Venta Bruta", value: Number(totals.venta_bruta) || 0 },
    { label: "Anulaciones", value: Number(totals.venta_anulada) || 0 },
    { label: "Venta Neta", value: venta },
    { label: "Premios", value: premios },
    { label: "Premios Pagados", value: pagado },
    { label: "Premios Por Pagar", value: porPagar },
    { label: "Total", value: total, strong: true },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pos-reports-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BarChart3 size={18} className="text-primary"/> Cuadre de Ventas{taqLabel && <span className="font-normal text-muted-foreground" data-testid="pos-reports-taquilla-label">· Taquilla: {taqLabel}</span>}</DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg border border-border bg-secondary/30 p-3 items-end" data-testid="pos-reports-filters">
          <div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="pos-reports-date-from"/></div>
          <div className="space-y-1"><Label className="text-xs">Hasta</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="pos-reports-date-to"/></div>
          <div className="space-y-1">
            <Label className="text-xs">Moneda</Label>
            <div className="flex gap-1.5">
              {Object.entries(CUR).map(([c, cfg]) => (
                <button key={c} type="button" onClick={() => { setCurrency(c); setApplied((a) => ({ ...a, currency: c })); }} data-testid={`pos-reports-currency-${c}`} aria-pressed={currency === c}
                  className={`flex-1 rounded-md border-2 px-2 py-1.5 text-xs font-bold transition-colors ${currency === c ? cfg.active : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={doFilter} disabled={isFetching} data-testid="pos-reports-filter-button"><Filter size={15} className="mr-1.5"/>Filtrar</Button>
        </div>

        <div id="pos-reports-print-area" className="space-y-4">
          <p className="text-xs text-muted-foreground" data-testid="pos-reports-scope">
            {taqLabel ? `Taquilla: ${taqLabel} · ` : ""}Rango: {applied.date_from} a {applied.date_to} (hora Caracas) · Moneda: {CUR[applied.currency].label}
          </p>
          {isError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="pos-reports-error">
              {error?.response?.data?.detail || "No se pudo cargar el cuadre."}
            </p>
          )}
          {isCop && (
            <p className="rounded-md border border-[#8f1433]/40 bg-[#8f1433]/10 px-3 py-2 text-xs text-[#8f1433] dark:text-rose-200" data-testid="pos-reports-cop-note">
              Arqueo en Pesos Colombianos: solo tickets emitidos en COP, montos en $ COP (enteros).
            </p>
          )}
          {!isError && (<>
          {/* Tabla 1: resumen financiero */}
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm" data-testid="pos-reports-summary-table">
              <thead><tr className="text-[11px] uppercase tracking-wide text-muted-foreground text-left bg-secondary/40"><th className="px-4 py-2">Descripción</th><th className="px-4 py-2 text-right">Monto</th></tr></thead>
              <tbody>
                {RESUMEN.map((r) => (
                  <tr key={r.label} data-testid={`pos-reports-row-${r.label.toLowerCase().replace(/\s+/g, "-")}`} className={`border-t border-border/60 ${r.strong ? "font-bold bg-primary/5" : ""}`}>
                    <td className="px-4 py-2">{r.label}</td>
                    <td className={`px-4 py-2 text-right tabular-nums ${r.strong ? "text-primary" : ""}`}>{fmt(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tabla 2: desglose por juego */}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm" data-testid="pos-reports-bygame-table">
              <thead><tr className="text-[11px] uppercase tracking-wide text-muted-foreground text-left bg-secondary/40">
                <th className="px-4 py-2">Juego</th>
                <th className="px-4 py-2 text-right">Venta</th>
                <th className="px-4 py-2 text-right">Premios</th>
                <th className="px-4 py-2 text-right">Utilidad</th>
              </tr></thead>
              <tbody>
                {isLoading && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Cargando…</td></tr>}
                {!isLoading && porJuego.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Sin ventas en el rango.</td></tr>}
                {porJuego.map((r) => (
                  <tr key={r.label} className="border-t border-border/60">
                    <td className="px-4 py-2 font-medium">{r.label}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(r.venta)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-amber-500 dark:text-amber-300">{fmt(r.premios)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-emerald-500 dark:text-emerald-300">{fmt(r.utilidad)}</td>
                  </tr>
                ))}
                {porJuego.length > 0 && (
                  <tr className="border-t-2 border-border font-bold bg-primary/5">
                    <td className="px-4 py-2">Total</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(totJuegoVenta)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(totJuegoPrem)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(totJuegoUtil)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </>)}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={doPrint} disabled={isError || isFetching} data-testid="pos-reports-print-button"><Printer size={15} className="mr-1.5"/>Imprimir</Button>
          <Button variant="outline" onClick={onClose} data-testid="pos-reports-close-button">Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
